import { Dispatch, SetStateAction, useMemo, useState } from "react";

import { Button } from "@/design-system/components/Button/Button";
import { Input } from "@/design-system/components/Input/Input";
import {
  Dialog,
  DialogActionsWrapper,
} from "@/design-system/components/Dialog/Dialog";

import { RoleAdmin, RoleInviteDefault } from "@/components/users/inviteUser";

import { UserEnvironmentSelectorInviteModal } from "./UserEnvironmentSelectorInviteModal";
import isEmailValid from "../isEmailValid";
import RoleSelector from "../RoleSelector";

type InviteUserModalProps = {
  open: boolean;
  onClose: () => void;
  onEmailChange: (email: string) => void;
  inviteUserRole: string;
  onRoleChange: (role: string) => void;
  onInvite: () => void;
  inviteSelectedEnvs: Record<string, boolean> | null;
  setInviteSelectedEnvs: Dispatch<
    SetStateAction<Record<string, boolean> | null>
  >;
  setHasEmailError: (hasError: boolean) => void;
};

function InviteUserModal({
  open,
  onClose,
  onEmailChange,
  inviteUserRole,
  onRoleChange,
  onInvite,
  inviteSelectedEnvs,
  setInviteSelectedEnvs,
  setHasEmailError,
}: InviteUserModalProps) {
  const [email, setEmail] = useState<string>("");

  const emailIsValid = useMemo(() => {
    return !isEmailValid(email);
  }, [email]);

  const emailErrorMessage = useMemo(() => {
    if (email.trim() === "") {
      return "Email is required.";
    }

    if (emailIsValid) {
      return "Email provided is not valid.";
    }

    return "";
  }, [email, emailIsValid]);

  const handleEmailChange = (email: string) => {
    setEmail(email);

    if (!isEmailValid(email)) {
      setHasEmailError(true);
      return;
    }

    setHasEmailError(false);
    onEmailChange(email);
  };

  return (
    <Dialog
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      open={open}
      title="Invite teammate"
      contentProps={{
        className: "w-2xl",
      }}
    >
      <div>
        <p className="font-bold" id="invite-user-email-label">
          Email
        </p>
        <Input
          className="w-full"
          type="email"
          name="invite-user-email"
          aria-labelledby="invite-user-email-label"
          value={email}
          error={
            emailErrorMessage
              ? { type: "manual", message: emailErrorMessage }
              : undefined
          }
          onChange={(e) => handleEmailChange(e.target.value.trim())}
        />
        <>
          <RoleSelector
            defaultRole={RoleInviteDefault}
            onRoleChange={(newRole) => onRoleChange(newRole)}
          />
          {inviteUserRole && inviteUserRole !== RoleAdmin && (
            <UserEnvironmentSelectorInviteModal
              inviteSelectedEnvs={inviteSelectedEnvs}
              setInviteSelectedEnvs={setInviteSelectedEnvs}
            />
          )}
        </>
      </div>
      <DialogActionsWrapper>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button disabled={emailErrorMessage !== ""} onClick={onInvite}>
          Send invite
        </Button>
      </DialogActionsWrapper>
    </Dialog>
  );
}

export default InviteUserModal;
